"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Calendar as CalendarIcon, Users, CircleCheck, ArrowRight, ArrowLeft, Loader, ShieldCheck, CreditCard, Clock, AlertCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
const VEHICLE_OPTIONS = ["Executive Sedan", "Executive SUV", "Premium SUV", "Luxury Sedan — $250", "Executive SUV — $350", "Stretch Limousine — $600", "Sprinter Van — $400", "Minivan — $300", "Other"];
const EXTRAS_OPTIONS = ["Child seat", "Meet & Greet", "Extra stop"];
export default function ReserveYourRide() {
  return <section data-section-id="130389" id="instant-luxury-quote" className="dark bg-background text-foreground relative min-h-screen py-24 overflow-hidden flex items-center">
      <div id="book-now" className="sr-only" />
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-background/95 backdrop-blur-sm z-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background z-10" />
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/luxury_car_night_city.png" alt="GoldSteer Luxury Chauffeur Service" className="w-full h-full object-cover opacity-30 grayscale contrast-125" />
      </div>

      <div className="container relative z-20 mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight mb-4 text-foreground">
            Reserve Your <span className="text-primary italic">Chauffeur</span>
          </h2>
          <p className="text-muted-foreground text-lg font-sans max-w-2xl mx-auto">
            Complete the form below and we will confirm your booking within the hour. No surprises, no hidden fees.
          </p>
        </div>

        <FormProvider formKey="instant_quote_form_home" formId="instant_quote_form_home" sectionName="booking-hero" defaultValues={{
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        pickup_address: "",
        pickup_date: "",
        vehicle_class: "",
        passengers: 1,
        extras: [],
        order_notes: "",
        privacy_consent: false,
        utm_source: "",
        referrer: ""
      }} validationRules={{
        first_name: {
          required: true
        },
        last_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        pickup_address: {
          required: true
        },
        pickup_date: {
          required: true
        },
        vehicle_class: {
          required: true
        },
        passengers: {
          required: true,
          min: 1
        },
        privacy_consent: {
          required: true
        }
      }} submitText="Request Quote" successMessage="Your executive ride request has been received. Our dispatch team will confirm your chauffeur details shortly." errorMessage="There was an error processing your reservation. Please verify your details or contact our 24/7 support.">
          <BookingFormContent />
        </FormProvider>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center border-t border-primary/20 pt-8">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <ShieldCheck className="size-6 text-primary" />
            <span className="text-xs font-mono font-medium uppercase tracking-widest">100% On-Time Guarantee</span>
          </div>
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Clock className="size-6 text-primary" />
            <span className="text-xs font-mono font-medium uppercase tracking-widest">24/7 Bay Area Dispatch</span>
          </div>
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <CreditCard className="size-6 text-primary" />
            <span className="text-xs font-mono font-medium uppercase tracking-widest">Secure Payments</span>
          </div>
        </div>
      </div>
    </section>;
}
function BookingFormContent() {
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const progressValue = step / totalSteps * 100;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  if (isSubmitted && !submitError) {
    return <Card className="bg-card/40 backdrop-blur-xl border-primary/30 shadow-2xl shadow-primary/10 rounded-none">
        <CardContent className="p-12 text-center flex flex-col items-center">
          <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 border border-primary/30">
            <CircleCheck className="size-10 text-primary" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-foreground mb-4">Request Received</h3>
          <p className="text-muted-foreground mb-8 max-w-md font-sans">
            {successMessage}
          </p>
          <Button onClick={() => {
          resetForm();
          setStep(1);
        }} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 rounded-none font-mono uppercase tracking-widest">
            New Request
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-card/60 backdrop-blur-2xl border-primary/20 shadow-2xl overflow-hidden rounded-none">
      <div className="bg-muted/10 p-6 border-b border-primary/10">
        <div className="flex justify-between text-xs font-mono font-medium uppercase tracking-widest text-muted-foreground mb-4">
          <span className={step >= 1 ? "text-primary" : ""}>1. Contact</span>
          <span className={step >= 2 ? "text-primary" : ""}>2. Journey Details</span>
          <span className={step >= 3 ? "text-primary" : ""}>3. Preferences</span>
        </div>
        <Progress value={progressValue} className="h-1 bg-muted rounded-none" />
      </div>

      <CardContent className="p-6 md:p-8">
        <Form onSubmit={handleSubmit}>
          {}
          <div className={step === 1 ? "block space-y-6 animate-in fade-in slide-in-from-right-4 duration-500" : "hidden"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="first_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="last_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="your@email.com" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (415) 000-0000" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {}
          <div className={step === 2 ? "block space-y-6 animate-in fade-in slide-in-from-right-4 duration-500" : "hidden"}>
            <FormField control={control} name="pickup_address" render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">
                    <MapPin className="size-3 text-primary" /> Pickup Address
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="SFO Terminal 3, Hotel, or Office" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="pickup_date" render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">
                      <SafeHydrate><CalendarIcon className="size-3 text-primary" /></SafeHydrate> Pickup Date
                    </FormLabel>
                    <FormControl>
                      <Input type="date" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="passengers" render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">
                      <Users className="size-3 text-primary" /> Passengers
                    </FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="1" className="bg-input/20 border-primary/20 focus:border-primary rounded-none" {...field} onChange={e => field.onChange(parseInt(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {}
          <div className={step === 3 ? "block space-y-6 animate-in fade-in slide-in-from-right-4 duration-500" : "hidden"}>
            <FormField control={control} name="vehicle_class" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Vehicle Class</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-input/20 border-primary/20 focus:border-primary rounded-none w-full">
                        <SelectValue placeholder="Select vehicle class" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-card border-primary/20 rounded-none">
                      {VEHICLE_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="focus:bg-primary/10">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="extras" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Extras</FormLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {EXTRAS_OPTIONS.map((option, i) => <div key={option} data-index={i} className="flex items-center space-x-2 bg-input/10 p-3 border border-primary/10">
                        <input type="checkbox" id={`extra-${i}`} className="size-4 accent-primary" checked={Array.isArray(field.value) && field.value.includes(option)} onChange={e => {
                  const current = Array.isArray(field.value) ? field.value : [];
                  const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
                  field.onChange(updated);
                }} />
                        <label htmlFor={`extra-${i}`} className="text-sm font-sans text-muted-foreground cursor-pointer select-none">
                          {option}
                        </label>
                      </div>)}
                  </div>
                </FormItem>} />

            <FormField control={control} name="order_notes" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Order Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Any special instructions or flight numbers?" className="bg-input/20 border-primary/20 focus:border-primary rounded-none min-h-[100px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="privacy_consent" render={({
            field
          }) => <FormItem className="space-y-3">
                  <div className="flex items-start space-x-3 p-4 bg-primary/5 border border-primary/10">
                    <FormControl>
                      <input type="checkbox" className="size-4 mt-1 accent-primary" checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-sans text-foreground cursor-pointer">
                        I have read and accept the privacy policy
                      </FormLabel>
                      <FormDescription className="text-[10px] italic">
                        I agree to the <Link to="#" newTab className="text-primary underline">privacy policy</Link>.
                      </FormDescription>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>} />

            {}
            <input type="hidden" {...control.register("utm_source")} />
            <input type="hidden" {...control.register("referrer")} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3">
                <AlertCircle className="size-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-xs font-sans text-destructive-foreground">{submitError}</p>
              </div>}
          </div>

          <div className="flex items-center justify-between mt-10 pt-6 border-t border-primary/10">
            <Button type="button" variant="outline" onClick={prevStep} disabled={step === 1 || isSubmitting} className="border-primary/20 text-muted-foreground hover:bg-primary/5 rounded-none font-mono uppercase tracking-widest text-xs">
              <ArrowLeft className="mr-2 size-3" /> Previous
            </Button>

            {step < totalSteps ? <Button type="button" onClick={nextStep} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 font-mono uppercase tracking-widest text-xs">
                Next Step <ArrowRight className="ml-2 size-3" />
              </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 shadow-lg shadow-primary/20 font-mono uppercase tracking-widest text-xs">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 size-3" />
                    Processing...
                  </> : 'Request Luxury Quote'}
              </Button>}
          </div>
        </Form>
      </CardContent>
    </Card>;
}